/**
 * HomePage — the Vegr landing page.
 *
 * The hero is ONE merged scroll-driven narrative containing both videos
 * (V1 source + V2 destination) stacked on top of each other with a
 * crossfade between them. No visible seam — the user scrolls through one
 * continuous experience and the videos handoff invisibly.
 *
 *   ScrollVideoNarrative   700vh    5 cards   light bg → dark bg at end
 *   ────────────────────   ─────    ────────  ────────────────────────
 *   • V1 plays during the first half (cards 1–3)
 *   • Crossfade at 48–52% of scroll (V1 fades out, V2 fades in)
 *   • V2 plays during the second half (cards 4–5)
 *   • Bg transitions to dark at 85–95% for the handoff into the dark
 *     marketing sections below
 *
 * After the hero, the marketing sections continue below:
 *   - Stacked post-hero panels:
 *     - TwoSidedCTA    (light sender/carrier split interaction)
 *     - Categories     (the six things you can ship)
 *     - AboutVegr      (pre-launch trust and clarity)
 *   - PrelaunchSignup  (early access email + role form)
 *   - LandingFooter    (contact info)
 */

import { AboutVegr } from "@/features/landing/components/AboutVegr";
import { Categories } from "@/features/landing/components/Categories";
import { LandingFooter } from "@/features/landing/components/LandingFooter";
import { PrelaunchSignup } from "@/features/landing/components/PrelaunchSignup";
import { ScrollVideoNarrative } from "@/features/landing/components/ScrollVideoNarrative";
import { TwoSidedCTA } from "@/features/landing/components/TwoSidedCTA";
import {
  HERO_BG_TRANSITION,
  HERO_CARDS,
  HERO_CROSSFADE_RANGE,
} from "@/features/landing/heroCards";

export default function HomePage() {
  return (
    <>
      {/* Single merged hero — both videos crossfade inside one sticky section */}
      <ScrollVideoNarrative
        videos={[
          {
            src: "/hero/vegr-hero-scrub.mp4",
            mobileSrc: "/hero/vegr-hero-scrub-mobile.mp4",
            poster: "/hero/vegr-hero-poster.jpg",
          },
          {
            src: "/hero/vegr-hero-destination.mp4",
            mobileSrc: "/hero/vegr-hero-destination-mobile.mp4",
            poster: "/hero/vegr-hero-destination-poster.jpg",
          },
        ]}
        cards={HERO_CARDS}
        heightVh={700}
        crossfadeRange={HERO_CROSSFADE_RANGE}
        bgTransition={HERO_BG_TRANSITION}
        showScrollHint
      />

      {/* Stacked post-hero panels on desktop. Mobile uses normal flow so long
          sections like Categories cannot be covered before their content ends. */}
      <div className="relative bg-slate-950">
        <div className="lg:sticky lg:top-0 lg:z-10">
          <TwoSidedCTA />
        </div>
        <div className="lg:sticky lg:top-0 lg:z-20">
          <Categories />
        </div>
        <div className="lg:sticky lg:top-0 lg:z-30">
          <AboutVegr />
        </div>
      </div>

      <PrelaunchSignup />
      <LandingFooter />
    </>
  );
}
